import React, { HTMLAttributes, memo, useEffect, useState } from 'react';

import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';

import { Text } from '@components/common';
import { Label } from '@components/fields';
import { Basic } from '@components/shared/Skeleton/Basic';
import { Forms } from '@components/SurveyBuilder';
import * as Enums from '@components/SurveyBuilder/types/enums';
import * as Models from '@components/SurveyBuilder/types/models';

import { surveyBuilderApi } from '../../api';
import { getFormHelpers } from '../../hooks/useFormHelpers';

import { FormExample } from './../FormExample';
import { BlockableComponentRenderer } from './BlockableComponentRenderer';
import { BlockSettingsForm } from './BlockSettingsForm';
import { Header } from './Header';

interface Props {
  activeId?: number;
}

export const DEBOUNCE_DELAY = 500;

const BlockSettingsComponent: React.FC<React.PropsWithChildren<Props>> = ({ activeId }) => {
  const {
    data: block,
    isFetching,
    isLoading
  } = surveyBuilderApi.useGetSurveyBuilderBlockQuery(
    {
      blockId: activeId as number
    },
    {
      skip: !activeId,
      refetchOnMountOrArgChange: true
    }
  );

  const [updateBlockable] = surveyBuilderApi.useUpdateSurveyBuilderBlockableMutation();
  const [updateBlockableSettings] = surveyBuilderApi.useUpdateSurveyBuilderBlockableSettingsMutation();

  const [blockable, setBlockable] = useState<Models.Blockable | undefined>(block?.blockable);

  const { getExample, getHelper, getLabel } = getFormHelpers(block);

  const example = getExample();
  const helper = getHelper();
  const label = getLabel();

  const update = async ({ config, settings }: { config: Forms.Data['config']; settings: Forms.Data['settings'] }) => {
    if (block && blockable) {
      const newBlockable = await updateBlockable({
        id: blockable.id,
        kind: block.kind,
        config
      }).unwrap();

      let newSettings: NonNullable<Models.Blockable['settings']> = {};

      if (settings && Object.keys(settings).length > 0) {
        newSettings = await updateBlockableSettings({
          id: blockable.id,
          kind: block.kind,
          settings
        }).unwrap();
      }

      if ([Enums.Kind.prototypeTest, Enums.Kind.websiteTest].includes(block.kind as Enums.Kind)) {
        setBlockable((prev) => ({ ...prev, ...newBlockable, settings: { ...prev?.settings, ...newSettings } }));
      }
    }
  };

  const { callback: debouncedUpdate } = useDebouncedCallback(update, DEBOUNCE_DELAY);

  const onChange = ({ settings, ...data }: Forms.Data['config'] & { settings: Forms.Data['settings'] }) => {
    setBlockable((prev) => {
      if (!prev) return prev;

      const { settings: prevSettings, ...rest } = prev;

      const settingsChanged = Object.keys(settings || {}).length > 0;

      return {
        ...rest,
        ...data,
        id: prev.id,
        ...(settingsChanged && { settings: { ...prevSettings, ...(settings || {}) } })
      };
    });

    update({ config: data, settings });
  };

  const debouncedChange = ({ settings, ...data }: Forms.Data['config'] & { settings: Forms.Data['settings'] }) => {
    setBlockable((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        ...data,
        id: prev.id,
        settings: { ...prev.settings, ...(settings || {}) }
      };
    });

    debouncedUpdate({ config: data, settings });
  };

  useEffect(() => {
    if (block?.blockable) {
      setBlockable(block.blockable);
    }
  }, [block]);

  if (!block || isFetching || isLoading || !activeId || activeId < 0) {
    return (
      <section className='flex h-full flex-col items-stretch'>
        <header className='flex h-20 flex-row items-center justify-between border-b border-gray-200 p-6'>
          <Basic height={30} width={90} />
        </header>

        <section className='h-full overflow-y-scroll p-6'>
          <Basic width={35} height={20} className='mb-2' />
          <Basic width='100%' height={42} className='mb-6' />
          <Basic width={90} height={20} className='mb-2' />
          <Basic width='100%' height={240} />
        </section>
      </section>
    );
  }

  return (
    <section className='flex h-full w-full flex-col'>
      <Header key={block.id} block={block} />
      <div className='flex min-h-0 flex-1 flex-col overflow-y-scroll p-6'>
        <BlockSettingsForm key={block.id} block={block} />

        <fieldset className='min-w-0'>
          {label && <Label className='mb-1 text-base'>{label}</Label>}

          {helper.response && (
            <Text h='400' color='gray-500' className='mb-2'>
              {helper.response}
            </Text>
          )}

          {block && blockable && (
            <BlockableComponentRenderer
              key={activeId}
              block={block}
              onChange={onChange}
              debouncedChange={debouncedChange}
              value={blockable}
            />
          )}

          {example.response && <FormExample className='mb-2 mt-4'>{example.response}</FormExample>}
        </fieldset>
      </div>
    </section>
  );
};

export const BlockSettings = memo(BlockSettingsComponent);
